import type {
  FieldMapping,
  MetafieldMappingTemplate,
  MetafieldSource,
  SpecCatalogEntry,
  StoreMetafieldDefinitionOption,
} from '../../services/integrationApi';
import type { StatusTone } from '../ui';

/** Prefix of a spec row's `sourceField`, as the API spells it (`src/config/metafieldSources.ts`). */
export const SPEC_SOURCE_PREFIX = 'spec:';

/** The spec mapping's writable types, mirrored from the API so a spec row can show its fit. */
const SPEC_TARGET_TYPES: readonly string[] = [
  'single_line_text_field',
  'multi_line_text_field',
  'number_integer',
  'number_decimal',
  'boolean',
  'list.single_line_text_field',
];

export const definitionsById = (definitions: StoreMetafieldDefinitionOption[]): Map<string, StoreMetafieldDefinitionOption> =>
  new Map(definitions.map((definition) => [`${definition.namespace}.${definition.key}`, definition]));

/**
 * Spec sources for the picker: the catalog's most-used labels plus any spec a
 * row already names, so a saved row never shows as "unknown".
 */
export function specSourcesFromCatalog(catalog: SpecCatalogEntry[], rows: FieldMapping[], limit = 40): MetafieldSource[] {
  const labels = new Set<string>();
  for (const row of rows) {
    if (row.sourceField.startsWith(SPEC_SOURCE_PREFIX)) labels.add(row.sourceField.slice(SPEC_SOURCE_PREFIX.length));
  }
  for (const entry of [...catalog].sort((a, b) => b.productCount - a.productCount || a.label.localeCompare(b.label))) {
    if (labels.size >= limit) break;
    if (entry.label.trim()) labels.add(entry.label.trim());
  }
  return Array.from(labels).map((label) => ({
    id: `${SPEC_SOURCE_PREFIX}${label}`,
    label,
    group: 'spec',
    kind: 'text',
    description: 'A catalog spec value, as the spec table writes it.',
    targetTypes: SPEC_TARGET_TYPES,
  }));
}

export type MappingRowStateCode = 'writes' | 'noSource' | 'noTarget' | 'noDefinition' | 'typeMismatch' | 'needsFlag';

export interface MappingRowState {
  code: MappingRowStateCode;
  tone: StatusTone;
  /** The definition type the row would need at its target, when one is known. */
  expectedType?: string;
}

/** What one row would do on the next sync, for its status chip. */
export function mappingRowState(
  row: FieldMapping,
  source: MetafieldSource | undefined,
  byId: Map<string, StoreMetafieldDefinitionOption>,
): MappingRowState {
  if (!row.sourceField || !source) return { code: 'noSource', tone: 'caution' };
  if (!row.targetField) return { code: 'noTarget', tone: 'caution', expectedType: source.targetTypes[0] };
  const definition = byId.get(row.targetField);
  if (!definition) return { code: 'noDefinition', tone: 'caution', expectedType: source.targetTypes[0] };
  if (!source.targetTypes.includes(definition.type)) return { code: 'typeMismatch', tone: 'caution', expectedType: source.targetTypes[0] };
  if (source.needsCompatibilityFlag) return { code: 'needsFlag', tone: 'info' };
  return { code: 'writes', tone: 'positive' };
}

/** Rows the next sync would write: a known source onto a defined target of a type it fits. */
export function writingRowCount(
  rows: FieldMapping[],
  sourceById: Map<string, MetafieldSource>,
  byId: Map<string, StoreMetafieldDefinitionOption>,
): number {
  return rows.filter((row) => {
    const state = mappingRowState(row, sourceById.get(row.sourceField), byId);
    return state.code === 'writes' || state.code === 'needsFlag';
  }).length;
}

/** The template's rows added to the table; a target the table already names keeps its row. */
export function applyTemplateRows(rows: FieldMapping[], template: MetafieldMappingTemplate): FieldMapping[] {
  const taken = new Set(rows.map((row) => row.targetField.toLowerCase()).filter(Boolean));
  const added: FieldMapping[] = [];
  for (const row of template.rows) {
    const id = row.targetField.toLowerCase();
    if (taken.has(id)) continue;
    taken.add(id);
    added.push({ sourceField: row.sourceField, targetField: row.targetField });
  }
  return [...rows.filter((row) => row.sourceField || row.targetField), ...added];
}
